import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { HistogramData } from "../../utils/AnalytivalDataType";
import { Box, useMediaQuery } from "@mui/material";

// Register necessary Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface HistogramChartProps {
  data: HistogramData;
  jobTitle?: string;
}

const HistogramChart: React.FC<HistogramChartProps> = ({ data, jobTitle }) => {
  const isMobile = useMediaQuery("(max-width:1200px)");

  // Parse percentage data
  const parsedData = Object.values(data)?.map((percentage) =>
    parseFloat(percentage.replace("%", ""))
  );

  // Create gradient for bars
  const createGradient = (ctx: CanvasRenderingContext2D, index: number) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    const colors = [
      { start: "rgba(16, 185, 129, 0.9)", end: "rgba(16, 185, 129, 0.3)" },
      { start: "rgba(59, 130, 246, 0.9)", end: "rgba(59, 130, 246, 0.3)" },
      { start: "rgba(168, 85, 247, 0.9)", end: "rgba(168, 85, 247, 0.3)" },
      { start: "rgba(236, 72, 153, 0.9)", end: "rgba(236, 72, 153, 0.3)" },
      { start: "rgba(251, 146, 60, 0.9)", end: "rgba(251, 146, 60, 0.3)" },
    ];
    const color = colors[index % colors.length];
    gradient.addColorStop(0, color.start);
    gradient.addColorStop(1, color.end);
    return gradient;
  };

  // Prepare chart data
  const chartData = {
    labels: Object.keys(data), // Use the range as labels
    datasets: [
      {
        label: `${jobTitle} Jobs in Netherlands`,
        data: parsedData,
        backgroundColor: function (context: any) {
          const ctx = context.chart.ctx;
          const index = context.dataIndex;
          return createGradient(ctx, index);
        },
        borderColor: "rgba(16, 185, 129, 0.8)",
        borderWidth: 0,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          font: {
            size: 13,
            weight: "bold" as const,
            family: "'Inter', sans-serif",
          },
          color: "#273848",
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "#273848",
        bodyColor: "#273848",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: "bold" as const,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (context: any) => {
            return ` Percentage: ${context.parsed.y}%`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
          color: "#64748b",
          maxRotation: 45,
          minRotation: 0,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: "rgba(148, 163, 184, 0.15)",
          drawBorder: false,
        },
        beginAtZero: true,
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          color: "#64748b",
          padding: 8,
          callback: function (value: any) {
            return `${value}%`;
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: isMobile ? 250 : 320,
        padding: isMobile ? 2 : 3,
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      }}
    >
      <Bar data={chartData} options={options} />
    </Box>
  );
};

export default HistogramChart;
